import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { Job } from '../../types/backend.types';
import { geofenceService } from '../../services/geofence.service';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { FileText, Search, ChevronDown, Calendar, User, Package, CheckCircle, Clock, X, Wrench, Users, Truck, Image, Download, Share2, Trash2, AlertTriangle } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use local file to avoid CDN issues
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Add spinner animation
const spinnerStyles = document.createElement('style');
spinnerStyles.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('[data-spinner-styles]')) {
  spinnerStyles.setAttribute('data-spinner-styles', '');
  document.head.appendChild(spinnerStyles);
}

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
  hours_worked: number;
}

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  serial_number: string;
  bradanna_equipment_number: string;
  hours_used: number;
  notes?: string;
}

interface TruckTicket {
  ticket_number: string;
  truck_company: string;
  material_type: string;
  ocr_material: string;
  ocr_ticket_number: string;
  ocr_vendor: string;
  created_at: string;
  verified_ticket_url?: string | null;
  employee_first_name: string;
  employee_last_name: string;
  fill_loads?: number;
  baserock_loads?: number;
  rock_loads?: number;
  asphalt_loads?: number;
  unknown_loads?: number;
}

interface MaterialTotals {
  fill_loads_total: number;
  baserock_loads_total: number;
  rock_loads_total: number;
  asphalt_loads_total: number;
  unknown_loads_total: number;
}

interface EquipmentIssue {
  issue_id: number;
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  issue_description: string;
  severity: string;
  reported_date: string;
  is_fixed: number;
  issue_photos: string | null;
  reporter_first_name: string;
  reporter_last_name: string;
}

interface DailyReport {
  report_id: number;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  foreman_first_name: string;
  foreman_last_name: string;
  report_date: string;
  load_count: number;
  crew_count: number;
  status: string;
  created_at: string;
  weather?: string;
  temperature?: number;
  work_completed_description?: string;
  materials_used?: string;
  issues?: string;
  accidents?: string;
  visitors?: string;
  tomorrow_plan?: string;
  photo_urls?: string;
  job_needs?: string | null;
  job_needs_other_text?: string | null;
  silt_fence_condition?: string;
  silt_fence_notes?: string;
  silt_fence_photos?: string;
  silt_fence_repair_status?: string;
  approved_by?: number | null;
  approver_first_name?: string | null;
  approver_last_name?: string | null;
  approved_at?: string | null;
  employees?: Employee[];
  equipment?: Equipment[];
  truck_tickets?: TruckTicket[];
  material_totals?: MaterialTotals;
  monthly_rainfall?: number;
  rainfall_month?: string;
  equipment_issues?: EquipmentIssue[];
}

export const AdminDailyReports: React.FC = () => {
  const routerLocation = useRouterLocation();
  const auth = useContext(AuthContext);
  const { location: gpsLocation, requestLocation } = useLocation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [viewingDate, setViewingDate] = useState<string>(''); // Which date we're viewing reports for
  const [showingAllReports, setShowingAllReports] = useState(false); // True when showing all reports (not filtered by job)
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Admin and Foreman access control
  if (!auth?.user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  const allowedRoles = ['admin', 'foreman'];
  if (!allowedRoles.includes(auth.user.role)) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators and foremen.</p>
      </div>
    );
  }

  useEffect(() => {
    loadJobs();
    loadAllReportsForDate(); // Load reports automatically on first load
  }, []);

  // Helper function to check if user is within a job's geofence
  const isWithinJobGeofence = useCallback((job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      return geofenceService.isInsideCircle(userLat, userLng, job.lat, job.lng, job.geofence_data.radius);
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      return geofenceService.isInsidePolygon(userLat, userLng, job.geofence_data.points);
    }
    // Default: 100m radius
    const distance = geofenceService.calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  }, []);

  useEffect(() => {
    // Try to get user's current job from: navigation state, time entry, or GPS location
    const getCurrentJob = async () => {
      // 1. Check if job was passed via navigation state first
      if (routerLocation.state?.jobId && jobs.length > 0) {
        console.log('✅ Job passed via navigation:', routerLocation.state);
        const job = jobs.find(j => j.job_id === routerLocation.state.jobId);
        if (job) {
          setSelectedJob(job);
          setSearchTerm(`${job.job_name} - ${job.bradanna_job_number}`);
          return;
        }
      }

      // 2. Try to get job from active time entry
      try {
        const response = await api.get(API_CONFIG.ENDPOINTS.CURRENT_TIME_ENTRY);
        if (response.data.success && response.data.data) {
          const currentJobId = response.data.data.job_id;
          const job = jobs.find(j => j.job_id === currentJobId);
          if (job) {
            setSelectedJob(job);
            setSearchTerm(`${job.job_name} - ${job.bradanna_job_number}`);
            console.log('✅ Job found from time entry:', job.job_name);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to load current job from time entry:', err);
      }

      // 3. Use GPS to auto-select job if admin is at a job site
      console.log('📍 Checking GPS location for job auto-select...');
      const loc = await requestLocation();
      if (loc && jobs.length > 0) {
        // Find the closest job the user is within the geofence of
        const jobsWithGeofence = jobs
          .filter(job => job.lat && job.lng)
          .map(job => ({
            ...job,
            isWithin: isWithinJobGeofence(job, loc.latitude, loc.longitude),
            distance: geofenceService.calculateDistance(loc.latitude, loc.longitude, job.lat, job.lng)
          }))
          .filter(job => job.isWithin)
          .sort((a, b) => a.distance - b.distance);

        if (jobsWithGeofence.length > 0) {
          const nearestJob = jobsWithGeofence[0];
          console.log('✅ Job found via GPS location:', nearestJob.job_name);
          setSelectedJob(nearestJob);
          setSearchTerm(`${nearestJob.job_name} - ${nearestJob.bradanna_job_number}`);
          return;
        }
        console.log('📍 GPS available but not at any job site');
      }

      // Admin not clocked in and not at a job site - they will manually select a job
      console.log('Admin not clocked in and not at job site - will manually select job');
    };

    if (jobs.length > 0) {
      getCurrentJob();
    }
  }, [jobs, auth.user?.role, routerLocation.state, requestLocation, isWithinJobGeofence]);

  useEffect(() => {
    if (selectedJob) {
      loadReports();
    }
  }, [selectedJob]);

  // Load PDF when report is selected
  useEffect(() => {
    const loadPdf = async () => {
      if (!selectedReport) {
        setPdfUrl(null);
        setNumPages(null);
        setPdfError(null);
        return;
      }

      try {
        setLoadingPdf(true);
        setPdfError(null);
        const token = localStorage.getItem('bfa_token');

        console.log('Fetching PDF for report:', selectedReport.report_id);
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/daily-reports/generate-pdf/${selectedReport.report_id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        console.log('PDF fetch response status:', response.status);
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        console.log('PDF blob size:', blob.size, 'type:', blob.type);
        const url = URL.createObjectURL(blob);
        console.log('PDF blob URL created:', url);
        setPdfUrl(url);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setPdfError(error instanceof Error ? error.message : 'Failed to load PDF');
      } finally {
        setLoadingPdf(false);
      }
    };

    loadPdf();
  }, [selectedReport]);

  // Clean up blob URL when component unmounts or PDF changes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const loadJobs = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.JOBS);
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  // Determine which date to show based on current time (EST)
  const getReportDate = () => {
    // Get current time in EST/EDT using Intl API
    const estFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false
    });

    const parts = estFormatter.formatToParts(new Date());
    const estYear = parts.find(p => p.type === 'year')?.value;
    const estMonth = parts.find(p => p.type === 'month')?.value;
    const estDay = parts.find(p => p.type === 'day')?.value;
    const estHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');

    // If before 8 PM EST (20:00), show previous day
    if (estHour < 20) {
      // Calculate yesterday's date in EST using local date math
      const estDate = new Date(parseInt(estYear!), parseInt(estMonth!) - 1, parseInt(estDay!));
      estDate.setDate(estDate.getDate() - 1);
      const year = estDate.getFullYear();
      const month = String(estDate.getMonth() + 1).padStart(2, '0');
      const day = String(estDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Otherwise show current EST day
    return `${estYear}-${estMonth}-${estDay}`;
  };

  const loadAllReportsForDate = async () => {
    setLoading(true);
    setShowingAllReports(true);
    const reportDate = getReportDate();
    setViewingDate(reportDate);

    try {
      const response = await api.get(`${API_CONFIG.ENDPOINTS.DAILY_REPORTS}?date=${reportDate}`);
      if (response.data.success) {
        setReports(response.data.data);
        // Clear selected report when viewing all reports (user can manually select)
        setSelectedReport(null);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    if (!selectedJob) return;

    setLoading(true);
    setShowingAllReports(false);
    setViewingDate(''); // Clear date when viewing by job

    try {
      const response = await api.get(`${API_CONFIG.ENDPOINTS.DAILY_REPORTS}?job_id=${selectedJob.job_id}`);
      if (response.data.success) {
        const loadedReports = response.data.data;
        setReports(loadedReports);

        // Clear selected report when job changes - user must manually select a report to view
        setSelectedReport(null);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReport) return;

    setDeleting(true);
    try {
      await api.delete(`${API_CONFIG.ENDPOINTS.DAILY_REPORTS}/${selectedReport.report_id}`);

      // Close modals and refresh list
      setShowDeleteConfirm(false);
      setSelectedReport(null);
      setPdfUrl(null);

      // Refresh the reports list
      if (showingAllReports) {
        loadAllReportsForDate();
      } else if (selectedJob) {
        loadReports();
      }
    } catch (err) {
      console.error('Failed to delete report:', err);
      alert('Failed to delete report. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectJob = (job: Job) => {
    setSelectedJob(job);
    setSearchTerm(`${job.job_name} - ${job.bradanna_job_number}`);
    setShowJobDropdown(false);
  };

  const handleClearJob = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedJob(null);
    setSearchTerm('');
    setShowJobDropdown(true);
    // Reload all reports for the date when job is cleared
    loadAllReportsForDate();
  };

  const handleViewReport = (report: DailyReport) => {
    setSelectedReport(report);
  };

  const filteredJobs = searchTerm
    ? jobs.filter(job =>
        job.job_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.bradanna_job_number?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : jobs;

  const formatDate = (dateStr: string) => {
    // Parse date string as local date, not UTC
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return '#22c55e';
      case 'submitted':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getWeatherIcon = (weather?: string) => {
    switch(weather?.toLowerCase()) {
      case 'sunny': return '☀️';
      case 'cloudy': return '☁️';
      case 'rainy': return '🌧️';
      case 'snowy': return '❄️';
      default: return '🌤️';
    }
  };

  const parsePhotos = (photoUrls?: string): string[] => {
    if (!photoUrls) return [];
    try {
      return JSON.parse(photoUrls);
    } catch {
      return [];
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '32px',
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '16px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    dropdownContainer: {
      position: 'relative' as const
    },
    dropdownInput: {
      width: '100%',
      padding: '14px 40px 14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#1f2937'
    },
    dropdownIcon: {
      position: 'absolute' as const,
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none' as const,
      color: '#6b7280'
    },
    dropdownList: {
      position: 'absolute' as const,
      top: 'calc(100% + 8px)',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      maxHeight: '400px',
      overflowY: 'auto' as const,
      zIndex: 100,
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
    },
    dropdownItem: {
      padding: '14px 16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      transition: 'all 0.15s',
      fontSize: '16px',
      fontWeight: '500',
      color: '#374151'
    },
    reportsList: {
      display: 'grid',
      gap: '16px'
    },
    reportCard: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      padding: '20px',
      border: '1px solid #e5e7eb',
      transition: 'all 0.2s',
      cursor: 'pointer'
    },
    reportHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    reportDate: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1f2937'
    },
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      color: 'white',
      textTransform: 'capitalize' as const
    },
    reportDetails: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px'
    },
    detailItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#6b7280',
      fontSize: '15px'
    },
    detailLabel: {
      fontWeight: '500',
      color: '#374151'
    },
    noData: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      color: '#9ca3af',
      fontSize: '16px'
    },
    loading: {
      textAlign: 'center' as const,
      padding: '60px 40px',
      color: '#6b7280',
      fontSize: '16px',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      gap: '20px'
    },
    loadingSpinner: {
      color: '#3b82f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    loadingText: {
      fontSize: '16px',
      fontWeight: '500' as const,
      color: '#6b7280'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Daily Reports</h1>
        <p style={styles.subtitle}>View and manage daily job reports</p>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>
          <Search size={20} />
          Select Job
        </div>

        <div style={styles.dropdownContainer}>
          <input
            type="text"
            style={styles.dropdownInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowJobDropdown(true)}
            onClick={() => setShowJobDropdown(true)}
            placeholder="Search jobs..."
          />
          <div
            style={{
              ...styles.dropdownIcon,
              cursor: 'pointer',
              pointerEvents: 'auto' as const
            }}
            onClick={handleClearJob}
            title="Clear selection"
          >
            <ChevronDown size={20} />
          </div>

          {showJobDropdown && (
            <div style={styles.dropdownList}>
              {filteredJobs.length > 0 ? (
                filteredJobs.map(job => (
                  <div
                    key={job.job_id}
                    style={styles.dropdownItem}
                    onClick={() => handleSelectJob(job)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {job.job_name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Job #{job.bradanna_job_number}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                  No jobs found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Show reports section when either job is selected OR showing all reports */}
      {(selectedJob || showingAllReports) && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            <FileText size={20} />
            {showingAllReports && viewingDate ? (
              <>
                Reports for {formatDate(viewingDate + 'T00:00:00')}
                <span style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '4px 12px',
                  borderRadius: '6px'
                }}>
                  All Jobs
                </span>
              </>
            ) : selectedJob ? (
              `Reports for ${selectedJob.job_name}`
            ) : (
              'Reports'
            )}
          </div>

          {loading ? (
            <div style={styles.loading}>
              <div style={styles.loadingSpinner}>
                <Clock size={48} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              <div style={styles.loadingText}>Loading reports...</div>
            </div>
          ) : reports.length > 0 ? (
            <div style={styles.reportsList}>
              {reports.map(report => (
                <div
                  key={report.report_id}
                  style={styles.reportCard}
                  onClick={() => handleViewReport(report)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.reportHeader}>
                    <div>
                      <div style={styles.reportDate}>
                        <Calendar size={18} style={{ display: 'inline', marginRight: '8px', color: '#3b82f6' }} />
                        {formatDate(report.report_date)}
                      </div>
                      {showingAllReports && (
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginTop: '4px' }}>
                          {report.job_name}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor(report.status)
                      }}
                    >
                      {report.status === 'approved' && <CheckCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />}
                      {report.status === 'submitted' && <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />}
                      {report.status}
                    </div>
                  </div>

                  <div style={styles.reportDetails}>
                    <div style={styles.detailItem}>
                      <User size={16} />
                      <span>
                        <span style={styles.detailLabel}>Created by:</span> {report.foreman_first_name} {report.foreman_last_name}
                      </span>
                    </div>

                    <div style={styles.detailItem}>
                      <Package size={16} />
                      <span>
                        <span style={styles.detailLabel}>Load Count:</span> {report.load_count}
                      </span>
                    </div>

                    <div style={styles.detailItem}>
                      <User size={16} />
                      <span>
                        <span style={styles.detailLabel}>Crew Count:</span> {report.crew_count}
                      </span>
                    </div>

                    {report.weather && (
                      <div style={styles.detailItem}>
                        <span>
                          <span style={styles.detailLabel}>Weather:</span> {report.weather}
                          {report.temperature && ` - ${report.temperature}°F`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.noData}>
              <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <div>No reports found for this job</div>
            </div>
          )}
        </div>
      )}

      {/* PDF Viewer Modal */}
      {selectedReport && (
        <div style={{
          position: 'fixed' as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: window.innerWidth < 768 ? '8px' : '20px'
        }}>
          {/* Action buttons */}
          <div style={{
            position: 'absolute' as const,
            top: window.innerWidth < 768 ? '12px' : '20px',
            right: window.innerWidth < 768 ? '12px' : '20px',
            display: 'flex',
            gap: '12px',
            zIndex: 10000
          }}>
            {/* Download button */}
            {pdfUrl && (
              <button
                onClick={async () => {
                  if (!selectedReport || !pdfUrl) return;

                  const fileName = `Daily_Report_${selectedReport.job_name.replace(/[^a-z0-9]/gi, '_')}_${new Date(selectedReport.report_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-')}.pdf`;

                  // Create download link
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{
                  width: window.innerWidth < 768 ? '40px' : '48px',
                  height: window.innerWidth < 768 ? '40px' : '48px',
                  backgroundColor: 'rgba(59, 130, 246, 0.9)',
                  border: '2px solid white',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Download PDF"
              >
                <Download size={window.innerWidth < 768 ? 20 : 24} />
              </button>
            )}

            {/* Share button */}
            {pdfUrl && (
              <button
                onClick={async () => {
                  if (!selectedReport || !pdfUrl) return;

                  const fileName = `Daily_Report_${selectedReport.job_name.replace(/[^a-z0-9]/gi, '_')}_${new Date(selectedReport.report_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-')}.pdf`;

                  // Try Web Share API first (mobile)
                  if (navigator.share && navigator.canShare) {
                    try {
                      const response = await fetch(pdfUrl);
                      const blob = await response.blob();
                      const file = new File([blob], fileName, { type: 'application/pdf' });

                      if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                          title: `Daily Report - ${selectedReport.job_name}`,
                          text: `Daily report for ${selectedReport.job_name} on ${new Date(selectedReport.report_date).toLocaleDateString()}`,
                          files: [file]
                        });
                        return;
                      }
                    } catch (err) {
                      console.log('Share failed, falling back to download:', err);
                    }
                  }

                  // Fallback to download
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{
                  width: window.innerWidth < 768 ? '40px' : '48px',
                  height: window.innerWidth < 768 ? '40px' : '48px',
                  backgroundColor: 'rgba(34, 197, 94, 0.9)',
                  border: '2px solid white',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Share PDF"
              >
                <Share2 size={window.innerWidth < 768 ? 20 : 24} />
              </button>
            )}

            {/* Delete button - Admin only */}
            {auth?.user?.role === 'admin' && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  width: window.innerWidth < 768 ? '40px' : '48px',
                  height: window.innerWidth < 768 ? '40px' : '48px',
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  border: '2px solid white',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete Report"
              >
                <Trash2 size={window.innerWidth < 768 ? 20 : 24} />
              </button>
            )}

            {/* Close button */}
            <button
              onClick={() => {
                setSelectedReport(null);
                setPdfUrl(null);
              }}
              style={{
                width: window.innerWidth < 768 ? '40px' : '48px',
                height: window.innerWidth < 768 ? '40px' : '48px',
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                border: '2px solid white',
                borderRadius: '50%',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close"
            >
              <X size={window.innerWidth < 768 ? 24 : 28} />
            </button>
          </div>

          {pdfError ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              gap: '20px',
              padding: '40px'
            }}>
              <div style={{
                color: '#ef4444',
                fontSize: 48
              }}>⚠️</div>
              <div style={{
                color: 'white',
                fontSize: 18,
                fontWeight: '500',
                textAlign: 'center'
              }}>
                Error Loading PDF
              </div>
              <div style={{
                color: '#cbd5e1',
                fontSize: 14,
                textAlign: 'center',
                maxWidth: '400px'
              }}>
                {pdfError}
              </div>
              <button
                onClick={() => {
                  setSelectedReport(null);
                  setPdfError(null);
                }}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          ) : loadingPdf ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              gap: '20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #fff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{
                color: 'white',
                fontSize: 18,
                fontWeight: '500'
              }}>
                Loading PDF...
              </div>
            </div>
          ) : pdfUrl ? (
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit={true}
              wheel={{ step: 0.1 }}
              pinch={{ step: 5 }}
              doubleClick={{ mode: 'reset' }}
            >
              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  overflow: 'auto'
                }}
                contentStyle={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: window.innerWidth < 768 ? '8px' : '20px',
                  paddingTop: window.innerWidth < 768 ? '60px' : '80px'
                }}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => {
                    console.log('PDF loaded successfully, pages:', numPages);
                    setNumPages(numPages);
                  }}
                  onLoadError={(error) => {
                    console.error('PDF.js error loading document:', error);
                    setPdfError(`PDF rendering error: ${error.message || 'Unknown error'}`);
                  }}
                  loading={
                    <div style={{ color: 'white', fontSize: '16px' }}>Loading PDF...</div>
                  }
                >
                  {numPages && Array.from(new Array(numPages), (el, index) => (
                    <div
                      key={`page_${index + 1}`}
                      style={{
                        marginBottom: '20px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <Page
                        pageNumber={index + 1}
                        renderTextLayer={true}
                        renderAnnotationLayer={false}
                        width={Math.min(window.innerWidth - (window.innerWidth < 768 ? 32 : 80), 800)}
                        loading={
                          <div style={{ color: 'white', fontSize: '14px', padding: '20px' }}>
                            Loading page {index + 1}...
                          </div>
                        }
                      />
                    </div>
                  ))}
                </Document>
              </TransformComponent>
            </TransformWrapper>
          ) : null}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedReport && (
        <div style={{
          position: 'fixed' as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <AlertTriangle size={32} color="#ef4444" />
            </div>
            <h3 style={{
              margin: '0 0 8px',
              fontSize: '20px',
              fontWeight: '700',
              color: '#111827'
            }}>
              Delete Daily Report?
            </h3>
            <p style={{
              margin: '0 0 24px',
              fontSize: '14px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              This will permanently delete the daily report for <strong>{selectedReport.job_name}</strong> on {new Date(selectedReport.report_date).toLocaleDateString()}. This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteReport}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

